import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  Column,
  Columns,
  Dialog,
  Hidden,
  SelectField,
  Stack,
  Text,
  TextField,
} from "@origin-digital/ods-core";
import { IconClose } from "@origin-digital/ods-icons";
import { IFormProps } from "../FormAccordionContainer";
import {
  connectableAustralianStates,
  streetTypeOptions,
  premiseTypes,
  streetSuffixes,
} from "./AddressConstants";
import {
  Field,
  FormikProps,
  FieldProps,
  FieldMetaProps,
  FieldInputProps,
  Formik,
  useFormikContext,
} from "formik";
import { premiseAddressDraftValidationSchema } from "./Validations";
import { PropertyType, State } from "../../Premises/types";
import { FormValues, PremiseAddressValues } from "../types";
import { formikFieldsBatchUpdater, generateAddressString } from "../helper";

interface IAddressDialogProps extends Partial<IFormProps> {
  open: boolean;
  setDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

const changeStateResetData = {
  buildingName: "",
  unitNumber: "",
  premiseType: "",
  streetNumber: "",
  lotNumber: "",
  streetName: "",
  grannyFlat: false,
  level: "",
  dpNumber: "",
  streetSuffix: "",
  streetType: "",
  blockNumber: "",
  sectionNumber: "",
};

const emptyLocalData = {
  suburb: "",
  postcode: "",
  state: "",
  ...changeStateResetData,
};

const AddressDialog = (props: IAddressDialogProps) => {
  const { open, setDialog } = props;
  const { values: premiseValues, setFieldValue } =
    useFormikContext<FormValues>();

  const handleApply = (newAddressData: PremiseAddressValues) => {
    setFieldValue("premiseAddress", newAddressData);
    setFieldValue(
      "premiseAddressString",
      generateAddressString(newAddressData)
    );
  };

  return (
    <Dialog
      id="address-dialog"
      open={open}
      title="Address"
      maxWidth="lg"
      onClose={() => setDialog(false)}
    >
      {/* Outer stack */}
      <Stack space="small">
        {/* Box hack to fix issue with TextField label being cropped by Dialog title */}
        <Box />
        {/* End hack */}

        {/* Inner stack */}
        <Formik
          initialValues={premiseValues.premiseAddress}
          validationSchema={() =>
            premiseAddressDraftValidationSchema(premiseValues)
          }
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={(v) => {
            handleApply(v);
            setDialog(false);
          }}
        >
          {({
            errors,
            submitForm,
            setFieldValue: setInnerFieldValue,
            values,
          }) => {
            return (
              <Stack>
                {/* Suburb, Postcode & State/Territory */}
                <Columns
                  collapseBelow="lg"
                  space={{
                    xs: "small",
                    lg: "xlarge",
                  }}
                >
                  <Column width="3/4">
                    <Columns collapseBelow="lg" space="medium">
                      <Column width="1/2">
                        <Field id="suburb" name="suburb">
                          {({
                            field,
                            meta,
                          }: {
                            field: FormikProps<FieldProps>;
                            meta: FieldMetaProps<FormValues>;
                          }) => {
                            return (
                              <TextField
                                {...field}
                                id="suburb"
                                variant="outlined"
                                label="Suburb"
                                error={!!meta.error}
                                helperText={meta.error}
                              />
                            );
                          }}
                        </Field>
                      </Column>
                      <Column>
                        <Columns space="medium">
                          <Column>
                            <Field id="postcode" name="postcode">
                              {({
                                field,
                                meta,
                              }: {
                                field: FieldInputProps<string>;
                                meta: FieldMetaProps<FormValues>;
                              }) => (
                                <TextField
                                  {...field}
                                  id="postcode"
                                  variant="outlined"
                                  label="Postcode"
                                  maxLength="4"
                                  error={!!meta.error}
                                  helperText={
                                    meta.error ? (
                                      meta.error
                                    ) : (
                                      <Columns>
                                        <Column />
                                        <Column width="content">
                                          {field.value.length}/4
                                        </Column>
                                      </Columns>
                                    )
                                  }
                                />
                              )}
                            </Field>
                          </Column>
                          <Column>
                            <SelectField
                              id="state"
                              variant="outlined"
                              label="State"
                              displayEmpty={true}
                              emptyAriaLabel="Empty"
                              value={values["state"]}
                              onChange={(e) => {
                                Object.keys(changeStateResetData).forEach(
                                  (k) => {
                                    setInnerFieldValue(
                                      `${k}`,
                                      changeStateResetData[
                                        k as keyof typeof changeStateResetData
                                      ]
                                    );
                                  }
                                );
                                setInnerFieldValue!("state", e.target.value);
                              }}
                              options={connectableAustralianStates}
                              error={!!errors.state}
                              helperText={errors.state}
                            />
                          </Column>
                        </Columns>
                      </Column>
                    </Columns>
                  </Column>
                  <Column width="1/4" />
                </Columns>
                {/* Suburb, Postcode & State/Territory */}

                {/* Rest of fields will be shown here once State is selected */}

                {/* Unit/Premise No, Type of Premise, Level & Building name */}
                {values["state"] ? (
                  <>
                    <Stack>
                      {values["state"] === State.NSW ? (
                        <Columns
                          collapseBelow="lg"
                          space={{
                            xs: "xsmall",
                            lg: "xlarge",
                          }}
                          alignY="stretch"
                        >
                          <Column width="3/4">
                            <Checkbox
                              id="grannyflat"
                              label="Granny flat"
                              checked={values["grannyFlat"]}
                              onChange={(e) => {
                                setInnerFieldValue(
                                  "grannyFlat",
                                  e.target.checked
                                );
                              }}
                            />
                          </Column>
                          <Column width="1/4" />
                        </Columns>
                      ) : null}

                      <Columns
                        collapseBelow="lg"
                        space={{
                          xs: "small",
                          lg: "xlarge",
                        }}
                        alignY="stretch"
                      >
                        <Column width="3/4">
                          <Columns
                            collapseBelow="lg"
                            space={{
                              xs: "xlarge",
                              lg: "medium",
                            }}
                          >
                            <Column>
                              <Columns space="medium">
                                <Column>
                                  <Field id="unitNumber" name="unitNumber">
                                    {({
                                      field,
                                      meta,
                                    }: {
                                      field: FormikProps<FieldProps>;
                                      meta: FieldMetaProps<FormValues>;
                                    }) => (
                                      <TextField
                                        {...field}
                                        id="unitNumber"
                                        variant="outlined"
                                        label="Unit/Premises No."
                                        error={!!meta.error}
                                        helperText={meta.error}
                                      />
                                    )}
                                  </Field>
                                </Column>
                                {premiseValues.propertyType ===
                                PropertyType.COMMERCIAL ? (
                                  <Column>
                                    <SelectField
                                      id="premiseType"
                                      variant="outlined"
                                      label="Premises type"
                                      displayEmpty={true}
                                      emptyAriaLabel="Empty"
                                      options={premiseTypes}
                                      value={values["premiseType"]}
                                      error={!!errors["premiseType"]}
                                      onChange={(e) => {
                                        setInnerFieldValue!(
                                          "premiseType",
                                          e.target.value
                                        );
                                      }}
                                    />
                                  </Column>
                                ) : null}
                              </Columns>
                            </Column>
                            <Column>
                              <Columns space="medium">
                                <Column>
                                  <TextField
                                    id="level"
                                    variant="outlined"
                                    label="Level"
                                    hideHelperTextSpace
                                    value={values["level"]}
                                    error={!!errors.level}
                                    onChange={(e) => {
                                      setInnerFieldValue(
                                        "level",
                                        e.target.value
                                      );
                                    }}
                                    helperText={errors.level}
                                  />
                                </Column>
                                <Column>
                                  <Field id="buildingName" name="buildingName">
                                    {({
                                      field,
                                    }: {
                                      field: FormikProps<FieldProps>;
                                    }) => (
                                      <TextField
                                        {...field}
                                        id="buildingName"
                                        variant="outlined"
                                        label="Building name"
                                        hideHelperTextSpace
                                      />
                                    )}
                                  </Field>
                                </Column>
                              </Columns>
                            </Column>
                          </Columns>
                        </Column>
                        <Column width="1/4">
                          <Box
                            display="flex"
                            alignItems="stretch"
                            height="full"
                          >
                            <>
                              <Hidden below="lg">
                                {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
                                <Box
                                  style={{ borderLeft: "1px solid #E3E3E3" }}
                                  paddingRight="medium"
                                  height="full"
                                />
                                {/* End Faux vertical <Divider> */}
                              </Hidden>
                              {premiseValues.propertyType ===
                              PropertyType.COMMERCIAL ? (
                                <Box
                                  alignSelf="center"
                                  paddingBottom={{
                                    xs: "large",
                                    lg: "none",
                                  }}
                                >
                                  <Text
                                    variant="body-small"
                                    tone="neutralLight"
                                  >
                                    Please specify the premise’s type if there’s
                                    a unit/premise number.
                                  </Text>
                                </Box>
                              ) : (
                                <Box
                                  alignSelf="center"
                                  paddingBottom={{
                                    xs: "large",
                                    lg: "none",
                                  }}
                                >
                                  <Text
                                    variant="body-small"
                                    tone="neutralLight"
                                  >
                                    Level and Building name is only required for
                                    multi-level projects.
                                  </Text>
                                </Box>
                              )}
                            </>
                          </Box>
                        </Column>
                      </Columns>
                      {/* Street No., Block, Section, DP */}
                      <Columns
                        collapseBelow="lg"
                        space={{
                          xs: "small",
                          lg: "xlarge",
                        }}
                        alignY="stretch"
                      >
                        <Column width="3/4">
                          <Columns collapseBelow="lg" space="medium">
                            <Column>
                              <Columns space="medium">
                                <Column>
                                  <Field id="streetNumber" name="streetNumber">
                                    {({
                                      field,
                                      meta,
                                    }: {
                                      field: FormikProps<FieldProps>;
                                      meta: FieldMetaProps<FormValues>;
                                    }) => (
                                      <TextField
                                        {...field}
                                        id="streetNumber"
                                        variant="outlined"
                                        label="Street No."
                                        helperText={
                                          meta.error
                                            ? meta.error
                                            : "e.g. 12 or 12A"
                                        }
                                        error={!!meta.error}
                                      />
                                    )}
                                  </Field>
                                </Column>
                                {values["state"] === State.ACT ? (
                                  <>
                                    <Column>
                                      <Field
                                        id="blockNumber"
                                        name="blockNumber"
                                      >
                                        {({
                                          field,
                                          meta,
                                        }: {
                                          field: FormikProps<FieldProps>;
                                          meta: FieldMetaProps<FormValues>;
                                        }) => (
                                          <TextField
                                            {...field}
                                            id="blockNumber"
                                            variant="outlined"
                                            label="Block No."
                                            error={!!meta.error}
                                            helperText={meta.error}
                                          />
                                        )}
                                      </Field>
                                    </Column>
                                  </>
                                ) : null}
                                {values["state"] === State.NSW ? (
                                  <Column>
                                    <Field id="lotNumber" name="lotNumber">
                                      {({
                                        field,
                                        meta,
                                      }: {
                                        field: FormikProps<FieldProps>;
                                        meta: FieldMetaProps<FormValues>;
                                      }) => (
                                        <TextField
                                          {...field}
                                          variant="outlined"
                                          id="lotNumber"
                                          label="Lot no."
                                          error={!!meta.error}
                                          helperText={meta.error}
                                        />
                                      )}
                                    </Field>
                                  </Column>
                                ) : null}
                              </Columns>
                            </Column>
                            <Column>
                              <Columns space="medium">
                                {values["state"] !== State.ACT &&
                                values["state"] !== State.NSW ? (
                                  <Column>
                                    <Field id="lotNumber" name="lotNumber">
                                      {({
                                        field,
                                        meta,
                                      }: {
                                        field: FormikProps<FieldProps>;
                                        meta: FieldMetaProps<FormValues>;
                                      }) => (
                                        <TextField
                                          {...field}
                                          variant="outlined"
                                          id="lotNumber"
                                          label="Lot no."
                                          error={!!meta.error}
                                          helperText={meta.error}
                                        />
                                      )}
                                    </Field>
                                  </Column>
                                ) : null}
                                {values["state"] === State.ACT ||
                                values["state"] === State.NSW ? (
                                  <Column>
                                    <Field
                                      id="sectionNumber"
                                      name="sectionNumber"
                                    >
                                      {({
                                        field,
                                        meta,
                                      }: {
                                        field: FormikProps<FieldProps>;
                                        meta: FieldMetaProps<FormValues>;
                                      }) => (
                                        <TextField
                                          {...field}
                                          id="sectionNumber"
                                          variant="outlined"
                                          label="Section No."
                                          hideHelperTextSpace
                                          error={!!meta.error}
                                          helperText={meta.error}
                                        />
                                      )}
                                    </Field>
                                  </Column>
                                ) : null}

                                {values["state"] === State.NSW ||
                                values["state"] === State.ACT ? (
                                  <Column>
                                    <Field id="dpNumber" name="dpNumber">
                                      {({
                                        field,
                                        meta,
                                      }: {
                                        field: FormikProps<FieldProps>;
                                        meta: FieldMetaProps<FormValues>;
                                      }) => (
                                        <TextField
                                          {...field}
                                          id="dpNumber"
                                          variant="outlined"
                                          label={`DP no. ${
                                            values["state"] === State.NSW
                                              ? ""
                                              : "if known"
                                          }`}
                                          error={!!meta.error}
                                          helperText={meta.error}
                                          hideHelperTextSpace
                                        />
                                      )}
                                    </Field>
                                  </Column>
                                ) : null}
                              </Columns>
                            </Column>
                          </Columns>
                        </Column>
                        {(values["grannyFlat"] === true &&
                          values["state"] === State.NSW) ||
                        values["state"] !== State.NSW ? (
                          <Column width="1/4">
                            <Box
                              display="flex"
                              alignItems="stretch"
                              height="full"
                            >
                              <Hidden below="lg">
                                {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
                                <Box
                                  style={{ borderLeft: "1px solid #E3E3E3" }}
                                  paddingRight="medium"
                                  height="full"
                                />
                                {/* End Faux vertical <Divider> */}
                              </Hidden>
                              <Box
                                alignSelf="center"
                                paddingBottom={{
                                  xs: "large",
                                  lg: "none",
                                }}
                              >
                                <Stack>
                                  {values["grannyFlat"] === true ? (
                                    <Text
                                      variant="body-small"
                                      tone="neutralLight"
                                    >
                                      Granny flats usually have a letter in the
                                      street no. (e.g. 12A rather than 12).
                                    </Text>
                                  ) : null}
                                  {values["state"] === State.NSW ? null : (
                                    <Text
                                      variant="body-small"
                                      tone="neutralLight"
                                    >
                                      {values["state"] === State.ACT
                                        ? "For ACT, provide street or block number. Also, provide section number."
                                        : "Street number is preferable. Enter lot number only if the street number isn’t known yet."}
                                    </Text>
                                  )}
                                </Stack>
                              </Box>
                            </Box>
                          </Column>
                        ) : null}
                      </Columns>
                      {/* Street name, Street type, Suffix */}
                      <Columns
                        collapseBelow="lg"
                        space={{
                          xs: "small",
                          lg: "xlarge",
                        }}
                      >
                        <Column width="3/4">
                          <Columns collapseBelow="lg" space="medium">
                            <Column>
                              <Field id="streetName" name="streetName">
                                {({
                                  field,
                                  meta,
                                }: {
                                  field: FormikProps<FieldProps>;
                                  meta: FieldMetaProps<FormValues>;
                                }) => (
                                  <TextField
                                    {...field}
                                    id="streetName"
                                    variant="outlined"
                                    label="Street name"
                                    error={!!meta.error}
                                    helperText={meta.error}
                                  />
                                )}
                              </Field>
                            </Column>
                            <Column>
                              <Columns space="medium">
                                <Column>
                                  <SelectField
                                    id="street-type"
                                    variant="outlined"
                                    label="Street type"
                                    displayEmpty={true}
                                    emptyAriaLabel="Empty"
                                    value={values["streetType"]}
                                    options={streetTypeOptions}
                                    onChange={(e) => {
                                      setInnerFieldValue(
                                        "streetType",
                                        e.target.value
                                      );
                                    }}
                                    error={!!errors["streetType"]}
                                    helperText={errors.streetType}
                                  />
                                </Column>
                                <Column>
                                  <SelectField
                                    id="street-suffix"
                                    variant="outlined"
                                    label="Suffix"
                                    displayEmpty={true}
                                    emptyAriaLabel="Empty"
                                    value={values["streetSuffix"]}
                                    options={streetSuffixes}
                                    onChange={(e) => {
                                      setInnerFieldValue!(
                                        "streetSuffix",
                                        e.target.value
                                      );
                                    }}
                                  />
                                </Column>
                              </Columns>
                            </Column>
                          </Columns>
                        </Column>
                        <Column width="1/4" />
                      </Columns>
                    </Stack>
                  </>
                ) : null}
                {/* End Street name, Street type, Suffix */}
                {/* Actions */}
                <Box
                  position={{
                    lg: "static",
                    xs: "fixed",
                  }}
                  paddingTop={{
                    lg: "medium",
                    xs: "medium",
                  }}
                  paddingBottom={{
                    lg: "none",
                    xs: "medium",
                  }}
                  paddingX={{
                    lg: "none",
                    xs: "medium",
                  }}
                  backgroundColor="white"
                  width="full"
                  style={{
                    left: "0",
                    bottom: "0",
                    zIndex: "1",
                  }}
                >
                  <Box
                    display="flex"
                    flexWrap={{
                      lg: "nowrap",
                      xs: "wrap",
                    }}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Button
                      variant="text"
                      size="small"
                      color="primary"
                      noTextPadding
                      onClick={() =>
                        formikFieldsBatchUpdater(
                          setInnerFieldValue,
                          emptyLocalData
                        )
                      }
                      icon={<IconClose color="inherit" />}
                    >
                      Clear
                    </Button>
                    <Box display="flex">
                      <Box marginRight="large">
                        <Button
                          variant="text"
                          size="small"
                          color="primary"
                          onClick={() => {
                            setDialog(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                      <Button
                        data-id="address-dialog-apply-button"
                        size="small"
                        onClick={() => {
                          submitForm();
                        }}
                      >
                        Apply
                      </Button>
                    </Box>
                  </Box>
                </Box>
                {/* End Actions */}
                {/* End Street No., Block, Section, DP */}
              </Stack>
            );
          }}
        </Formik>
        {/* End Inner stack */}
      </Stack>
    </Dialog>
  );
};

export default AddressDialog;
